import type {
  CdaDetailSubsection,
  CdaDetailSubsectionTable,
  CdaParsedCdataSectionNode,
  CdaParsedHtmlElementNode,
  CdaParsedHtmlNode,
  CdaParsedTextNode,
} from '@ctw/shared/utils/cda/types';
import { type ClassName, twx } from '@ctw/shared/utils/tailwind';
import type { NonEmptyArray } from '@ctw/shared/utils/types';

// https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
// Defining these here instead of referencing `Node.*` types makes them usable client-side and server-side
export const DOM_NODE_TYPE_ELEMENT = 1 as const;
export const DOM_NODE_TYPE_TEXT = 3 as const;
export const DOM_NODE_TYPE_CDATA_SECTION = 4 as const;

export const emptyTableMessage = 'This table is empty.';

export const isCdaDetailSubsectionTable = (
  subsection: CdaDetailSubsection,
): subsection is CdaDetailSubsectionTable => {
  return (
    Array.isArray((subsection as CdaDetailSubsectionTable).columns) &&
    Array.isArray((subsection as CdaDetailSubsectionTable).rows)
  );
};

// biome-ignore lint/suspicious/noControlCharactersInRegex: Checking for non-ASCII characters
export const NEEDS_NORMALIZATION_CHECK_REGEX = /[^\x00-\x7F]/;

const denylistedNarrativeNodeNames = ['footnote'] as const;
const isAllowedNarrativeNodeName = (nodeName: string): boolean =>
  !denylistedNarrativeNodeNames.includes(nodeName.toLowerCase() as never);

// `img` never has text content, but a `src` attribute makes it meaningful on its own —
// allowlisted here for the same reason as `br`/`hr`. `renderMultiMedia`'s own media likewise
// never lives as text content of the node — it's in a separate `observationMedia` entry,
// cross-referenced via its `referencedObject` attribute — so it's allowlisted for the same
// reason, whether or not it also carries a `caption` child. Entries are lowercase because
// `isAllowlistedEmptyNodeName` below lowercases the node name it checks against this list.
const allowlistedEmptyNodeNames = ['br', 'hr', 'img', 'rendermultimedia'] as const;
export const isAllowlistedEmptyNodeName = (nodeName: string) =>
  allowlistedEmptyNodeNames.includes(nodeName.toLowerCase() as never);

export const isDomHtmlElement = (node: Node): node is HTMLElement =>
  node.nodeType === DOM_NODE_TYPE_ELEMENT;

export const isDomTextNode = (node: Node): node is Text => node.nodeType === DOM_NODE_TYPE_TEXT;

export const isDomHtmlElementWithOnlyTextChildNodes = (node: Node): node is HTMLElement => {
  if (!isDomHtmlElement(node)) {
    return false;
  }

  const childNodes = node.childNodes;
  for (let i = 0; i < childNodes.length; i++) {
    if (!isDomTextNode(childNodes[i])) {
      return false;
    }
  }

  return true;
};

export const isDomNarrativeNode = (node: Node): node is Text | HTMLElement => {
  return (
    (isDomTextNode(node) ||
      (isDomHtmlElementWithOnlyTextChildNodes(node) &&
        isAllowedNarrativeNodeName(node.nodeName))) &&
    node.textContent.trim() !== ''
  );
};

export const isDomCdataSection = (node: Node): node is CDATASection =>
  node.nodeType === DOM_NODE_TYPE_CDATA_SECTION;

export const isParsedHtmlElement = (node: CdaParsedHtmlNode): node is CdaParsedHtmlElementNode =>
  node.nodeType === 'element';

export const isParsedTextNode = (node: CdaParsedHtmlNode): node is CdaParsedTextNode =>
  node.nodeType === 'text';

export const isParsedHtmlElementWithOnlyTextChildNodes = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode => {
  if (!isParsedHtmlElement(node)) {
    return false;
  }

  return !node.childNodes.some((child) => child.nodeType !== 'text');
};

export const isParsedNarrativeNode = (
  node: CdaParsedHtmlNode,
): node is CdaParsedTextNode | CdaParsedHtmlElementNode => {
  if (isParsedTextNode(node) && node.textContent.trim() !== '') {
    return true;
  }

  return (
    isParsedHtmlElementWithOnlyTextChildNodes(node) &&
    isAllowedNarrativeNodeName(node.nodeName) &&
    node.childNodes
      .map((child) => (isParsedTextNode(child) ? child.textContent : ''))
      .join('')
      .trim() !== ''
  );
};

export const isParsedCdataSection = (node: CdaParsedHtmlNode): node is CdaParsedCdataSectionNode =>
  node.nodeType === 'cdata_section';

export const isParsedTableElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode & { nodeName: 'table' } => node.nodeName === 'table';

const headingElementTagNames = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
type HeadingElementTagName = (typeof headingElementTagNames)[number];
export const isParsedHeadingElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode & { nodeName: HeadingElementTagName } =>
  headingElementTagNames.includes(node.nodeName as never);

const listElementTagNames = ['ol', 'ul', 'list'] as const;
type ListElementTagName = (typeof listElementTagNames)[number];
export const isParsedListElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode & { nodeName: ListElementTagName } =>
  listElementTagNames.includes(node.nodeName as never);

export const isParsedSpanElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode & { nodeName: 'span' } => node.nodeName === 'span';

const linkElementTagNames = ['htmlLink', 'a'] as const;
type LinkElementTagName = (typeof linkElementTagNames)[number];
export const isParsedLinkElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode & { nodeName: LinkElementTagName } =>
  linkElementTagNames.includes(node.nodeName as never);

const paragraphElementTagNames = ['p', 'paragraph'] as const;
type ParagraphElementTagName = (typeof paragraphElementTagNames)[number];
export const isParsedParagraphElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode & { nodeName: ParagraphElementTagName } =>
  paragraphElementTagNames.includes(node.nodeName as never);

export const isParsedDivElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode & { nodeName: 'div' } => node.nodeName === 'div';

export const isParsedDivTextElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode & { nodeName: 'div' } =>
  isParsedDivElement(node) && !node.childNodes.some(isParsedHtmlElement);

export const isParsedBrElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode & { nodeName: 'br' } => node.nodeName === 'br';

export const isParsedImageElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode & { nodeName: 'img' } => node.nodeName === 'img';

// MediaType subset a browser can render directly via `<img src="data:...">`. `image/tiff` is a
// real CDA `observationMedia` mediaType, but no browser decodes it from a bare `<img>` src, so a
// resolved-but-TIFF reference falls to the unsupported-media message alongside a non-image type.
// `image/jpg` isn't an IANA-registered type, but real-world CDAs emit it, and it must stay in
// sync with `ServerCdaParser.inlineRenderableImageMediaTypes`, which already accepts it — a
// mismatch here means a resolved `image/jpg` entry falls to the unsupported-media message instead
// of rendering.
const browserRenderableImageMediaTypes = [
  'image/gif',
  'image/jpeg',
  'image/jpg',
  'image/png',
] as const;
export const isBrowserRenderableImageMediaType = (mediaType: string): boolean =>
  browserRenderableImageMediaTypes.includes(mediaType as never);

export const isSafeParsedHtmlElement = (
  node: CdaParsedHtmlNode,
): node is CdaParsedHtmlElementNode =>
  isParsedHtmlElement(node) && htmlTagSafelist.includes(node.nodeName.toLowerCase() as never);

export const willNodeBeRenderedAsTextElement = (node: CdaParsedHtmlNode): boolean =>
  ['content', 'caption', 'footnote'].includes(node.nodeName as never) ||
  isParsedDivTextElement(node) ||
  isParsedSpanElement(node) ||
  isParsedCdataSection(node) ||
  isParsedTextNode(node);

/*
 * `isEffectivelyEmpty` is computed from trimmed text content alone (see the CDA parser), so a
 * subtree whose only content is an allowlisted-empty element (`img`/`br`/`hr`/`renderMultiMedia`
 * — see `isAllowlistedEmptyNodeName`) is indistinguishable from a genuinely empty one by that
 * flag alone. This recurses through the tree so a caller can ask "does this subtree have
 * anything worth rendering" once, instead of every wrapper/table-cell/table-row emptiness check
 * re-deriving its own (and risking forgetting the allowlist).
 */
export const nodeHasRenderableContent = (node: CdaParsedHtmlNode): boolean => {
  if (!node.isEffectivelyEmpty) {
    return true;
  }

  if (isParsedHtmlElement(node) && isAllowlistedEmptyNodeName(node.nodeName)) {
    return true;
  }

  if (node.nodeType !== 'element' && node.nodeType !== 'cdata_section') {
    return false;
  }

  return node.childNodes.some(nodeHasRenderableContent);
};

/*
 * Helper function which returns a boolean indicating if at least one of the given node names exists
 * in the given node's child tree.
 */
export const nodeChildTreeContainsNodeWithNames = (
  node: CdaParsedHtmlNode,
  targetNodeNames: Array<string>,
): boolean => {
  if (node.nodeType !== 'element' && node.nodeType !== 'cdata_section') {
    return false;
  }

  for (const child of node.childNodes) {
    if (child.nodeType === 'element' && targetNodeNames.includes(child.nodeName)) {
      return true;
    }

    if (
      (child.nodeType === 'element' || child.nodeType === 'cdata_section') &&
      nodeChildTreeContainsNodeWithNames(child, targetNodeNames)
    ) {
      return true;
    }
  }

  return false;
};

/*
 * Helper function which returns all nodes from the given node's child tree that have one of the given
 * node names.
 */
export const filterChildNodesByNodeName = (
  node: CdaParsedHtmlNode,
  targetNodeNames: NonEmptyArray<string>,
  recursive = true,
): Array<CdaParsedHtmlElementNode> => {
  if (node.nodeType !== 'element' && node.nodeType !== 'cdata_section') {
    return [];
  }

  const filteredChildNodes: Array<CdaParsedHtmlElementNode> = [];
  for (const child of node.childNodes) {
    if (child.nodeType === 'element' && targetNodeNames.includes(child.nodeName)) {
      filteredChildNodes.push(child);
    }

    if (recursive && (child.nodeType === 'element' || child.nodeType === 'cdata_section')) {
      const matchingChildNodes = filterChildNodesByNodeName(child, targetNodeNames, recursive);
      if (matchingChildNodes.length > 0) {
        filteredChildNodes.push(...matchingChildNodes);
      }
    }
  }

  return filteredChildNodes;
};

/*
 * Helper function which returns the text content of a node and its child tree.
 */
export const textContentFromNodeTree = (node: CdaParsedHtmlNode): string => {
  if (node.nodeType === 'text') {
    return node.textContent;
  }

  let textContent = '';
  for (const child of node.childNodes) {
    if (child.nodeType === 'text') {
      textContent += child.textContent;
    } else {
      textContent += textContentFromNodeTree(child);
    }
  }

  return textContent;
};

const htmlTagSafelist = [
  'a',
  'abbr',
  'acronym',
  'address',
  'area',
  'article',
  'aside',
  'b',
  'bdi',
  'big',
  'blockquote',
  'body',
  'br',
  'button',
  'caption',
  'center',
  'cite',
  'code',
  'col',
  'colgroup',
  'data',
  'datalist',
  'dd',
  'del',
  'details',
  'dfn',
  'dir',
  'div',
  'dl',
  'dt',
  'em',
  'fieldset',
  'figcaption',
  'figure',
  'font',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'head',
  'header',
  'hr',
  'html',
  'i',
  'img',
  'input',
  'ins',
  'kbd',
  'keygen',
  'label',
  'legend',
  'li',
  'main',
  'map',
  'mark',
  'menu',
  'menuitem',
  'meter',
  'nav',
  'ol',
  'optgroup',
  'option',
  'output',
  'p',
  'pre',
  'progress',
  'q',
  'rp',
  'rt',
  'ruby',
  's',
  'samp',
  'section',
  'select',
  'small',
  'span',
  'strike',
  'strong',
  'sub',
  'summary',
  'sup',
  'table',
  'tbody',
  'td',
  'textarea',
  'tfoot',
  'th',
  'thead',
  'time',
  'tr',
  'tt',
  'u',
  'ul',
  'var',
  'wbr',
] as const;

export const elementNamesIntentionallyOmittedFromRendering = [
  'renderMultiMedia', // https://build.fhir.org/ig/HL7/CDA-core-sd/narrative.html#rendermultimedia
] as const;

export const elementNamesThatDontAllowTextChildNodes = [
  'tr',
  'thead',
  'colgroup',
  'table',
  'tbody',
] as const;

interface UniqueIdentifierForNodeOptions {
  context: string;
  purpose: string;
  node: CdaParsedHtmlNode;
  index?: number;
}

export const uniqueIdentifierForNode = ({
  context,
  purpose,
  node,
  index,
}: UniqueIdentifierForNodeOptions) => {
  return `${context}-${purpose}-${node.nodeType === 'element' ? node.nodeName : node.nodeType}-${index ? `-${index}` : ''}`;
};

interface TableColumnStylesFromContentOptions {
  columnCount: number;
  columnTdElements: Array<CdaParsedHtmlElementNode>;
}

interface TableColumnStylesFromContentResult {
  minWidth: string;
  width: string;
  maxWidth: string;
  className: ClassName;
}

export const tableColumnStylesFromContent = ({
  columnCount,
  columnTdElements,
}: TableColumnStylesFromContentOptions): TableColumnStylesFromContentResult => {
  const columnCellTexts = columnTdElements.map((td) => textContentFromNodeTree(td));

  const columnHasCellTextExceeding350Characters = columnCellTexts.some(
    (cellText) => cellText.trim().length > 350,
  );
  const columnHasCellTextExceeding150Characters = columnCellTexts.some(
    (cellText) => cellText.trim().length > 150,
  );
  const columnHasCellTextExceeding25Characters = columnCellTexts.some(
    (cellText) => cellText.trim().length > 25,
  );
  const columnHasCellTextContainsSpaces = columnCellTexts.some((cellText) =>
    cellText.includes(' '),
  );

  const columnStyles = {
    minWidth: columnCount === 1 ? '100%' : '75px',
    width: columnCount === 1 ? '100%' : 'auto',
    maxWidth: columnCount === 1 ? '100%' : '550px',
    className: '',
  };

  /*
   * For text exceeding 150 characters:
   * - Set a minimum width of 150px since this text content deserves more space
   */
  if (columnHasCellTextExceeding150Characters) {
    columnStyles.minWidth = '250px';
  }

  /*
   * For text exceeding 350 characters:
   * - Set a minimum width of 450px since this text content deserves more space
   */
  if (columnHasCellTextExceeding350Characters) {
    columnStyles.minWidth = '450px';
  }

  /*
   * For text not exceeding 25 characters which contains spaces:
   * - Maintain minimum width but set a width of 0 so the cell shrinks to at most the minimum width
   */
  if (columnHasCellTextContainsSpaces && !columnHasCellTextExceeding25Characters) {
    columnStyles.width = '0px';
  }

  /*
   * For text not exceeding 25 characters which does not have spaces:
   * - Set a minimum width of 0 and width of 0 so the cell shrinks to fit the content
   */
  if (!(columnHasCellTextContainsSpaces && columnHasCellTextExceeding25Characters)) {
    columnStyles.minWidth = '0px';
    columnStyles.width = '0px';
  }

  return columnStyles;
};

interface TableCellStylesFromContentOptions {
  cellElement: CdaParsedHtmlElementNode | null | undefined;
}

interface TableCellStylesFromContentResult {
  className: ClassName;
}

export const tableCellStylesFromContent = ({
  cellElement,
}: TableCellStylesFromContentOptions): TableCellStylesFromContentResult => {
  if (!cellElement) {
    return { className: '' };
  }

  const cellText = textContentFromNodeTree(cellElement)?.trim() ?? '';
  const cellTextExceeds100Characters = cellText.length > 100;
  const cellTextExceeds25Characters = cellText.length > 25;
  const cellTextContainsSpaces = cellText.includes(' ');

  return {
    className: twx({
      /*** Remember: Precedence here matters! If logic results in conflicting styles, the last one wins. ***/
      /*
       * For text exceeding 100 characters without any spaces:
       * - Wrap, breaking words if necessary
       */
      '!whitespace-normal !text-pretty !break-all !wrap-break-word':
        cellTextExceeds100Characters && !cellTextContainsSpaces,
      /*
       * For text not exceeding 100 characters, or text which exceeds 100 characters but has spaces:
       * - Wrap on spaces/punctuation only
       */
      '!whitespace-normal !text-pretty !break-normal !wrap-normal': !(
        cellTextExceeds100Characters && !cellTextContainsSpaces
      ),
      /*
       * For text not exceeding 25 characters:
       * - Never wrap
       */
      '!whitespace-nowrap !text-nowrap': !cellTextExceeds25Characters,
    }),
  };
};

export const formatCdaDate = (cdaTimestamp: string): string | null => {
  if (!cdaTimestamp || typeof cdaTimestamp !== 'string') {
    return null;
  }

  const year = cdaTimestamp.substring(0, 4);
  const month = cdaTimestamp.substring(4, 6);
  const day = cdaTimestamp.substring(6, 8);

  if (!(year && month && day)) {
    return null;
  }

  return `${month}/${day}/${year}`;
};
